// API access for the superadmin console.
//
// Host resolution mirrors packages/web/src/lib/apiBase.ts: the console is a
// static SPA on its own subdomain, and Rails only answers on `api.`, so a
// relative /api call never reaches it in production.

const TOKEN_KEY = "console_auth_token";

export function apiBase(): string {
  const envUrl = (import.meta as { env?: Record<string, string | undefined> }).env?.VITE_API_URL;
  if (envUrl) return envUrl.replace(/\/$/, "");
  if (typeof window === "undefined") return "/api";

  const { hostname, protocol } = window.location;
  if (hostname === "localhost" || hostname === "127.0.0.1") return "/api";

  const labels = hostname.split(".");
  const root = labels.length > 2 ? labels.slice(1).join(".") : hostname;
  return `${protocol}//api.${root}/api`;
}

export function getToken(): string | null {
  return localStorage.getItem(TOKEN_KEY);
}

export function setToken(token: string | null) {
  if (token) localStorage.setItem(TOKEN_KEY, token);
  else localStorage.removeItem(TOKEN_KEY);
}

export class ApiError extends Error {
  status: number;
  constructor(message: string, status: number) {
    super(message);
    this.status = status;
  }
}

type Query = Record<string, string | number | boolean | undefined | null | string[] | Record<string, string>>;

function queryString(params?: Query): string {
  if (!params) return "";
  const search = new URLSearchParams();
  for (const [key, value] of Object.entries(params)) {
    if (value === undefined || value === null || value === "") continue;
    if (Array.isArray(value)) {
      // Repeated `key[]=` — Rack parses that into an Array; `key[0]=` would
      // arrive as a Hash and quietly break Array() on the Rails side.
      for (const item of value) search.append(`${key}[]`, String(item));
    } else if (typeof value === "object") {
      // filters: { status: "open" } -> filters[status]=open
      for (const [k, v] of Object.entries(value)) {
        if (v !== undefined && v !== null && v !== "") search.append(`${key}[${k}]`, String(v));
      }
    } else {
      search.append(key, String(value));
    }
  }
  const s = search.toString();
  return s ? `?${s}` : "";
}

async function request<T>(path: string, init: RequestInit = {}, params?: Query): Promise<T> {
  const token = getToken();

  let res: Response;
  try {
    res = await fetch(`${apiBase()}${path}${queryString(params)}`, {
      ...init,
      headers: {
        "Content-Type": "application/json",
        ...(token ? { Authorization: `Bearer ${token}` } : {}),
        ...(init.headers || {}),
      },
    });
  } catch {
    // fetch() rejects identically for a dead host, a DNS failure and a rejected
    // CORS preflight — the browser deliberately hides which, and the raw message
    // is "Failed to fetch". Say which host we could not reach and name the two
    // things that are actually ever wrong, so the next person does not have to
    // open devtools to find out.
    throw new ApiError(
      `Kunde inte nå API:t på ${apiBase()}. Kontrollera att backend är driftsatt ` +
        `och att den här adressen är tillåten som CONSOLE_ORIGIN.`,
      0,
    );
  }

  if (res.status === 401) {
    // The session died under us — drop the stale token so the app routes to login
    // instead of retrying forever behind a spinner.
    setToken(null);
    window.location.href = "/login";
    throw new ApiError("Sessionen har gått ut", 401);
  }

  const text = await res.text();
  const body = text ? JSON.parse(text) : {};
  if (!res.ok) throw new ApiError(body.error || `Fel ${res.status}`, res.status);
  return body as T;
}

export const api = {
  get: <T,>(path: string, params?: Query) => request<T>(path, { method: "GET" }, params),
  patch: <T,>(path: string, body: unknown) => request<T>(path, { method: "PATCH", body: JSON.stringify(body) }),
  post: <T,>(path: string, body?: unknown) =>
    request<T>(path, { method: "POST", body: body === undefined ? undefined : JSON.stringify(body) }),
  del: <T,>(path: string) => request<T>(path, { method: "DELETE" }),
};
